import '.a{display:flex;}.ssr-plugin'
import '.y{flex-direction:row;}.ssr-plugin'
import '.c{align-items:center;}.ssr-plugin'
import '.bb{gap:0.5rem;}.ssr-plugin'
import '.ae{background-color:var(--shades-white);}.ssr-plugin'
import '.bc{border:var(--blue-border);}.ssr-plugin'
import '.bd{box-shadow:0 0 10px 0 rgba(0, 0, 0, 0.20);}.ssr-plugin'
import '.be{border-radius:1.5rem;}.ssr-plugin'
import '.bf{padding:0.5rem 1rem 0.5rem 0.5rem;}.ssr-plugin'
import '.bg{width:fit-content;}.ssr-plugin'
import '.b8{cursor:pointer;}.ssr-plugin'
import '.bh{transition:opacity 300ms ease-in-out, transform 300ms ease-in-out, max-width 500ms ease-in-out;}.ssr-plugin'
import { CloseButton } from '@app/components/core/button/CloseButton'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import useMediaQuery from '@app/util/useMediaQuery'
import { useTranslations } from '@i18n/useLanguage'
import { styled } from 'inlines'
import { useCallback, useEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { ToastIcon } from './ToastIcon'

const ANIMATION_DURATION = 300

export type ToastType = 'success' | 'error'

export type ToastProps = {
  message: string
  type: ToastType
  hideAfterSeconds?: number
  isHideTimeoutPaused?: boolean
  onRemove: VoidFunction
}

export const Toast = ({
  type,
  message,
  hideAfterSeconds = 5,
  isHideTimeoutPaused = false,
  onRemove,
}: ToastProps) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app.common
  const [isExiting, setIsExiting] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const duration = hideAfterSeconds * 1000

  const triggerExit = useCallback(() => {
    setIsExiting(true)
    setTimeout(() => {
      onRemove()
    }, ANIMATION_DURATION)
  }, [onRemove])

  const startTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      triggerExit()
    }, duration)
  }, [duration, triggerExit])

  useEffect(() => {
    if (isHideTimeoutPaused) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      return
    }

    startTimer()

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [isHideTimeoutPaused, startTimer])

  return (
    <styled.div className="a y c bb ae bc bd be bf bg b8 bh"
      style={{
        maxWidth: isMobile ? 'calc(100vw - 1rem)' : '30rem',
        minWidth: isMobile ? 'calc(100vw - 1rem)' : '30rem',
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? 'translateX(20px)' : 'translateX(0)',
        '&:hover': {
          minWidth: '30rem',
          maxWidth: '80vw',
        },
      }}
    >
      <ToastIcon variant={type} />
      <Column gap={0} style={{ marginRight: '0.5rem', minWidth: 0 }}>
        <Title.H5 style={{ marginBottom: 0 }}>
          {match(type)
            .with('success', () => translations.toastSuccess)
            .with('error', () => translations.toastFailure)
            .exhaustive()}
        </Title.H5>
        <Text.Sized
          text={isMobile ? 'medium' : 'small'}
          color="var(--shades-blue-500)"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: isMobile ? 'wrap' : 'nowrap',
            width: '100%',
            display: 'block',
          }}
        >
          {message}
        </Text.Sized>
      </Column>
      <CloseButton size="medium" onClick={triggerExit} />
    </styled.div>
  )
}
